import { useCallback, useEffect, useRef, useState } from 'react';

// Display-only reservations. Authoritative balances/revisions continue to drive
// every API request and remain untouched by animation timing.
export const useWalletPresentation = (owner, scope) => {
  const [state, setState] = useState({ owner, scope, entries: {} });
  const active = useRef({ owner, scope }), mounted = useRef(false);
  active.current = { owner, scope };
  useEffect(() => {
    mounted.current = true;
    setState({ owner, scope, entries: {} });
    return () => { mounted.current = false; };
  }, [owner, scope]);
  const update = useCallback((change) => {
    if (!mounted.current || active.current.owner !== owner || active.current.scope !== scope) return;
    setState((current) => {
      const entries = current.owner === owner && current.scope === scope ? { ...current.entries } : {};
      change(entries);
      return { owner, scope, entries };
    });
  }, [owner, scope]);
  const reserveStake = useCallback((requestId, amount) => {
    if (Number.isSafeInteger(amount) && amount > 0) update((entries) => { entries[`request:${requestId}`] = amount; });
  }, [update]);
  const confirmWallet = useCallback((requestId, round) => update((entries) => {
    delete entries[`request:${requestId}`];
    if (round?.finished !== false && Number.isSafeInteger(round?.payout_minor) && round.payout_minor > 0) {
      entries[`payout:${round.game || 'keno'}:${round.request_id}`] = round.payout_minor;
    }
  }), [update]);
  const revealPayout = useCallback((round) => update((entries) => {
    delete entries[`payout:${round.game || 'keno'}:${round.request_id}`];
  }), [update]);
  const cancelStake = useCallback((requestId) => update((entries) => { delete entries[`request:${requestId}`]; }), [update]);
  const withheld = state.owner === owner && state.scope === scope ? Object.values(state.entries).reduce((sum, amount) => sum + amount, 0) : 0;
  const presentWallet = (wallet) => ({ ...wallet, balance_minor: Math.max(0, wallet.balance_minor - withheld) });
  return { reserveStake, confirmWallet, revealPayout, cancelStake, presentWallet };
};